/**
 * Show incoming online states one at a time with a minimum gap, so every
 * move gets its animation even when updates land close together. Your own
 * moves show at once. A long backlog (after a reconnect) jumps to the latest.
 */
import { useEffect, useRef, useState } from 'react';
import type { GameState } from '../../engine';

export function usePacedState(state: GameState, gapMs: number, me: number | null): GameState {
  const [shown, setShown] = useState(state);
  const queue = useRef<GameState[]>([]);
  const lastAt = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shownRef = useRef(state);

  useEffect(() => {
    const show = (s: GameState) => {
      shownRef.current = s;
      lastAt.current = Date.now();
      setShown(s);
    };
    if (gapMs <= 0) {
      show(state);
      return;
    }
    if (state === shownRef.current) return;
    // A different game or hole jump: show at once.
    const newest = state.log[state.log.length - 1];
    const mine = newest && 'player' in newest && newest.player === me;
    if (state.eventSeq < shownRef.current.eventSeq || state.seed !== shownRef.current.seed) {
      queue.current = [];
      show(state);
      return;
    }
    queue.current.push(state);
    if (queue.current.length > 6 || mine) {
      queue.current = [];
      show(state);
      return;
    }
    const pump = () => {
      timer.current = null;
      const next = queue.current.shift();
      if (!next) return;
      show(next);
      if (queue.current.length) timer.current = setTimeout(pump, gapMs);
    };
    if (!timer.current) {
      const wait = Math.max(0, lastAt.current + gapMs - Date.now());
      timer.current = setTimeout(pump, wait);
    }
  }, [state, gapMs, me]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return gapMs <= 0 ? state : shown;
}
